import Nprogress from 'nprogress'
import 'nprogress/nprogress.css'
import { useEffect, useState } from 'react'
import { safeSessionStorage } from 'ui'
import 'ui/dist/ui.css'
import { z } from 'zod'
import {
  finalSubmitData,
  loginPassChange,
  mpowerCookieConsentCookie,
  mpowerReferrer,
  opportunityId,
  showNotification,
  tempOppId,
} from '~/cookie'
import { authenticator } from '~/services/auth.server'
import { checkAccessRestrictions } from '~/services/checkAccessRestrictions.server'

import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import { useWindowScroll } from '@mantine/hooks'
import '@mantine/notifications/styles.css'
import { LinksFunction, LoaderFunction, json, redirect } from '@remix-run/node'
import { Outlet, useLocation, useNavigate } from '@remix-run/react'

import { Document } from './components/RootComponents/Document'
import Layout from './components/RootComponents/Layout'
import './global.css'
import { useLoaderActionData } from './hooks/useLoaderActionData'
import { RETRY_COUNT_KEY } from './hooks/useRetryOnError'
import { destroyActiveSession } from './services/destroyActiveSession.server'
import { restrictDashboard } from './utils/commonUtils'
import {
  LS_URL_RESTRICTION,
  RB_UNAVAILABLE_QUERY_PARAM,
  USER_ROLE,
  mpowerReferrerUrl,
  sessionCheckRoutes,
} from './utils/constant'
import { inferSafeParseErrors } from './utils/inferSafeParseErrors'
import { shouldShowCookieDisclaimer } from './utils/mpowerCookieConsent'
import { capturePageLoaded, capturePageView } from './utils/posthog'
import { UppercaseFirstLetter } from './utils/uppercaseFirstLetter'

export { ErrorBoundary } from './components/ErrorBoundary'

Nprogress.configure({
  showSpinner: false,
})

export const schema = z.object({
  softTuPullData: z.string().optional(),
})
type SchemaFields = z.infer<typeof schema>
type SchemaFieldsErrors = inferSafeParseErrors<typeof schema>
type ActionData = {
  fields: SchemaFields
  errors?: SchemaFieldsErrors
  softTuPullData?: any
}

const isRepeatBorrowerPath = (pathname: string) =>
  pathname === '/rb' || pathname.includes('/rb/') || pathname.endsWith('/rb')

export const loader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url)
  const pathname = url?.pathname || ''
  if (isRepeatBorrowerPath(pathname)) {
    throw redirect(`/dashboard?${RB_UNAVAILABLE_QUERY_PARAM}=1`)
  }
  const isDashboardPath = pathname === '/dashboard' || pathname?.startsWith('/dashboard/')
  const changePassCookie = (await loginPassChange?.parse(request?.headers?.get('Cookie'))) || {}
  if (isDashboardPath && changePassCookie?.changePass) {
    return redirect('/login/changePassword')
  }
  const softTuPullData = (await finalSubmitData.parse(request.headers.get('Cookie'))) || {}
  const cookie = (await opportunityId.parse(request.headers.get('Cookie'))) || {}
  const mpowerReferrerCookie = (await mpowerReferrer.parse(request.headers.get('Cookie'))) || {}
  const cookieHeader = request.headers.get('Cookie')
  const mpowerCookieConsent = (await mpowerCookieConsentCookie.parse(cookieHeader)) ?? null
  const showNotificationCookie = (await showNotification.parse(request.headers.get('Cookie'))) || {}
  const gtmId = process.env.GTM_ID
  let user = await authenticator.isAuthenticated(request)
  const endpoint = url?.pathname
  const headers = new Headers()
  if (sessionCheckRoutes?.includes(endpoint)) user = await destroyActiveSession(request, headers)
  const tempOppCookie = (await tempOppId?.parse(request.headers.get('Cookie'))) || {}
  const dashboardRedirectPath = restrictDashboard(user, url)
  if (dashboardRedirectPath) return redirect(dashboardRedirectPath)
  const accessControlResult = await checkAccessRestrictions(request, endpoint, LS_URL_RESTRICTION)
  const { shouldRedirect = false, user_details = {}, redirectToRoute } = accessControlResult || {}
  const { additional_fields = {}, show_lightspeed = false } = user_details || {}
  if (shouldRedirect && show_lightspeed && additional_fields?.is_lightspeed_user)
    return redirect(redirectToRoute)
  const isMpowerRefferer = mpowerReferrerCookie?.refererData === mpowerReferrerUrl
  const showCookieDisclaimer = shouldShowCookieDisclaimer(mpowerCookieConsent, cookieHeader)
  return json(
    {
      softTuPullData,
      gtmId,
      oppID: cookie.oppID,
      showHeader: user ? true : false,
      firstName: UppercaseFirstLetter(user?.userdetails?.firstName),
      lastName: UppercaseFirstLetter(user?.userdetails?.lastName),
      preferredName: UppercaseFirstLetter(user?.userdetails?.preferredname),
      serverURL: process?.env?.SERVER_URL,
      userRole: user?.userdetails?.role || '',
      userLoginType: user?.userdetails?.loginType || '',
      isResetFlow: user?.userdetails?.isResetFlow || false,
      tempOppId: tempOppCookie?.tempOppId || '',
      userEmail: user?.userdetails?.email || '',
      showNotificationCookie: showNotificationCookie || false,
      isMpowerRefferer: isMpowerRefferer || false,
      showCookieDisclaimer,
    },
    { headers },
  )
}

export const meta = () => {
  return [
    { title: 'Check Your Student Loan Eligibility | MPOWER Financing' },
    { charset: 'utf-8' },
    { viewport: 'width=device-width,initial-scale=1' },
  ]
}

export default function App() {
  const data = useLoaderActionData<ActionData>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const restrictedRoutes = ['personal', 'finance', 'employment', 'education']
  const headerPages = [
    'eligibility',
    'personal',
    'finance',
    'employment',
    'education',
    'id-verification',
    'dashboard',
    'inactive-applications',
    'logout',
    'continue',
    'my-loans',
    'hardship',
  ]
  const stepNineCompleted =
    data?.softTuPullData?.data?.final_submit?.borrowerStepProgress == 9 &&
    data?.oppID === data?.softTuPullData?.data?.final_submit?.oppID
      ? true
      : false
  const [, scrollTo] = useWindowScroll()
  const isURLBeforeFinalSubmit = restrictedRoutes.includes(pathname.split('/')[1])
  const showHeader = headerPages.includes(pathname.split('/')[1])
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && safeSessionStorage.isStorageAvailable()) {
        safeSessionStorage.removeItem(RETRY_COUNT_KEY)
      }
    } catch (err) {}
  }, [])

  useEffect(() => {
    if (pathname) {
      capturePageView(pathname, { path: location?.pathname })
      capturePageLoaded(pathname, data)
    }
    scrollTo({ y: 0 })
    if (stepNineCompleted && isURLBeforeFinalSubmit) {
      navigate('/in-review', { replace: true })
    }
  }, [pathname])

  useEffect(() => {
    const isUserTASorNEW =
      data?.userRole === USER_ROLE.tas || data?.userRole === USER_ROLE.preBorrower
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'oppID') {
        navigate('/dashboard')
      }
      if (event?.key == 'loggedIn' && showHeader) {
        if (isUserTASorNEW || data?.isResetFlow) return
        navigate('/login')
      }
    }
    window?.addEventListener('storage', handleStorage)
    return () => window?.removeEventListener('storage', handleStorage)
  }, [data?.userRole, data?.isResetFlow, showHeader, navigate])
  const [pageError, setPageError] = useState()
  return (
    <Document gtmId={data.gtmId}>
      <Layout showHeader={showHeader} showUser={data.showHeader} data={data} pageError={pageError}>
        <Outlet context={[pageError, setPageError]} />
      </Layout>
    </Document>
  )
}
